/**
 * slotChangeAdapter — `api/v1/student-slot-change` → les formes de « Modifier mes créneaux ».
 *
 * Le serveur tient toutes les règles : quels cours se changent, où il reste une place chez leurs
 * tuteurs, quels horaires il colorie pour quel tuteur, quel repli demander. L'adaptateur ne fait
 * que lire : il n'ajoute ni ne retire une place, une couleur ou un repli. Deux choses seulement
 * lui reviennent, parce qu'elles sont de la présentation :
 *  - la COULEUR de chaque tuteur colorié, un jeton du design system pris dans l'ordre de la liste
 *    des tuteurs (jamais un hex) ;
 *  - le rangement de la grille par jour, dans l'ordre de la semaine, chaque jour dans l'ordre de la
 *    journée, d'après le libellé de la famille (l'heure qu'elle vit).
 *
 * Les lignes inutilisables (sans id, sans clé) sont écartées, jamais jetées en erreur.
 *
 * API-aware ; jamais importé par le DS.
 */
import { type SemanticColorKey } from "../../../../../design-system/theme/tokens/colors"
import { formatShortWeekday, weekdayKey, weeklySlotParts } from "../../../ui/formatters/weeklySlotFormatter"
import {
    type UISlotChangeCourse,
    type UISlotChangeFallback,
    type UISlotChangeGrid,
    type UISlotChangeGridDay,
    type UISlotChangeGridSlot,
    type UISlotChangeOptions,
    type UISlotChangeOutcome,
    type UISlotChangePending,
    type UISlotChangeSeat,
    type UISlotChangeTutor,
    type UISubject,
    type UITutor,
} from "../../../ui/types"
import { toUITutor } from "../LiveSessions/liveSessionListAdapter"
import { toUISubject, type ApiSchoolSubject } from "../shared/schoolSubjectAdapter"

type ApiTutor = {
    id?: number | string
    firstname?: string | null
    avatar?: string | null
    gender?: string | null
} | null

export type ApiSlotChangeCourse = {
    live_session_id?: number | null
    day_time?: string | null
    label?: string | null
    duration?: number | null
    room_type?: string | null
    subjects?: ApiSchoolSubject[] | null
    tutor?: ApiTutor
    effective_on?: string | null
    effective_label?: string | null
    /** La clé du repli du cours, celle de `fallbacks[].key`. */
    fallback_key?: string | null
}

export type ApiSlotChangeSeat = {
    course_live_session_id?: number | null
    live_session_id?: number | null
    tutor?: ApiTutor
    day_time?: string | null
    label?: string | null
    seats_left?: number | null
    effective_on?: string | null
    subjects?: ApiSchoolSubject[] | null
    credited_weeks?: string[] | null
}

export type ApiSlotChangeGrid = {
    slots?:
        | {
              key?: string | null
              label?: string | null
              course?: boolean | null
              tutor_ids?: (number | string)[] | null
              course_live_session_ids?: number[] | null
          }[]
        | null
    tutors?: (NonNullable<ApiTutor> & { coloured?: boolean | null; busy?: string[] | null })[] | null
}

export type ApiSlotChangeFallback = {
    key?: string | null
    tutor?: ApiTutor
    subjects?: ApiSchoolSubject[] | null
    kept_live_session_ids?: number[] | null
    kept_label?: string | null
    kept_stops?: { label?: string | null; stops_on_label?: string | null }[] | null
    /** Le repli du serveur quand l'envoi n'en dit pas (le MCP) : l'app ne le lit pas, la famille choisit. */
    default?: string | null
}

/** `GET api/v1/student-slot-change` — sans ids, `courses` seuls ; avec, le reste aussi. */
export type ApiSlotChangePayload = {
    courses?: ApiSlotChangeCourse[] | null
    seats?: ApiSlotChangeSeat[] | null
    grid?: ApiSlotChangeGrid | null
    fallbacks?: ApiSlotChangeFallback[] | null
    min_slots?: number | null
    /** Un changement déjà en cours, ce que la famille suit : le parcours attend qu'il soit réglé. */
    pending?: ApiSlotChangePending | null
}

/** `GET api/v1/student-slot-change/pending` → `pending`, et le même champ de la lecture du parcours. */
export type ApiSlotChangePending = {
    status?: string | null
    status_line?: string | null
    courses?: { label?: string | null; stops_on_label?: string | null }[] | null
    proposed?: string[] | null
}

/** `POST api/v1/student-slot-change`. */
export type ApiSlotChangeOutcome = {
    outcome?: string | null
    changes?:
        | {
              from?: string | null
              to?: string | null
              tutor?: string | null
              tutor_gender?: string | null
              effective?: string | null
              effective_on?: string | null
          }[]
        | null
    credits?: number | null
    credited_weeks?: string[] | null
    tutors_notified?: boolean | null
    offers?: { id?: number | string; tutor?: ApiTutor; slots?: { key?: string; label?: string }[] | null }[] | null
    /** `{ label, tutor, stops_on_label }` ; un serveur d'avant écrivait la chaîne seule (« le lundi 17h »). */
    held?: (string | { label?: string | null; tutor?: ApiTutor; stops_on_label?: string | null })[] | null
    effective_on?: string | null
    effective_label?: string | null
    mail_sent?: boolean | null
}

/**
 * Les couleurs des tuteurs coloriés, dans l'ordre : des tons du design system qui portent une
 * tinte claire et son encre (`tintOnFill`), assez loin les uns des autres pour se distinguer, et
 * aucun ne lit « erreur » (pas de `danger`). Le concentré (`purple`) est réservé au live.
 */
export const SLOT_CHANGE_TUTOR_TONES: SemanticColorKey[] = ["success", "warning", "info", "accent"]

function tutorOf(raw: ApiTutor | undefined): UITutor | null {
    if (raw?.id == null) return null
    const tutor = toUITutor({ id: raw.id, firstname: raw.firstname ?? "", avatar: raw.avatar ?? undefined })
    return raw.gender === "homme" || raw.gender === "femme" ? { ...tutor, gender: raw.gender } : tutor
}

function subjectsOf(raw: ApiSchoolSubject[] | null | undefined): UISubject[] {
    return (raw ?? []).filter((subject) => subject?.id != null).map(toUISubject)
}

function ids(raw: (number | string | null | undefined)[] | null | undefined): number[] {
    return (raw ?? []).map(Number).filter((id) => Number.isFinite(id))
}

function labels(raw: (string | null | undefined)[] | null | undefined): string[] {
    return (raw ?? []).filter((label): label is string => typeof label === "string" && label !== "")
}

function genderOf(raw: string | null | undefined): "homme" | "femme" | undefined {
    return raw === "homme" || raw === "femme" ? raw : undefined
}

/** Une place gardée : l'objet du serveur, ou la chaîne d'un serveur d'avant, sans son article. */
function heldOf(
    raw: string | { label?: string | null; tutor?: ApiTutor; stops_on_label?: string | null } | null | undefined
) {
    if (typeof raw === "string") {
        const label = raw.replace(/^le\s+/i, "").trim()
        return label === "" ? null : { label, tutor: null, stopsOnLabel: null }
    }
    return raw?.label ? { label: raw.label, tutor: tutorOf(raw.tutor), stopsOnLabel: raw.stops_on_label || null } : null
}

export function toUISlotChangeCourses(rows: ApiSlotChangeCourse[] | null | undefined): UISlotChangeCourse[] {
    return (rows ?? [])
        .map((row): UISlotChangeCourse | null =>
            row?.live_session_id == null || !row.day_time
                ? null
                : {
                      liveSessionId: Number(row.live_session_id),
                      dayTime: row.day_time,
                      label: row.label || row.day_time,
                      duration: typeof row.duration === "number" && row.duration > 0 ? row.duration : 60,
                      subjects: subjectsOf(row.subjects),
                      tutor: tutorOf(row.tutor),
                      ...(row.fallback_key ? { fallbackKey: row.fallback_key } : {}),
                      effectiveOn: row.effective_on ?? null,
                  }
        )
        .filter((course): course is UISlotChangeCourse => course != null)
}

function toUISeats(rows: ApiSlotChangeSeat[] | null | undefined): UISlotChangeSeat[] {
    return (rows ?? [])
        .map((row): UISlotChangeSeat | null => {
            const tutor = tutorOf(row?.tutor)
            if (row?.course_live_session_id == null || row.live_session_id == null || tutor == null || !row.day_time) {
                return null
            }
            return {
                courseLiveSessionId: Number(row.course_live_session_id),
                liveSessionId: Number(row.live_session_id),
                tutor,
                dayTime: row.day_time,
                label: row.label || row.day_time,
                seatsLeft: typeof row.seats_left === "number" ? row.seats_left : null,
                effectiveOn: row.effective_on ?? null,
                subjects: subjectsOf(row.subjects),
                creditedWeeks: labels(row.credited_weeks),
            }
        })
        .filter((seat): seat is UISlotChangeSeat => seat != null)
}

/** Les tuteurs de la grille ; un tuteur colorié reçoit le ton suivant de la palette. */
function toUIGridTutors(rows: ApiSlotChangeGrid["tutors"]): UISlotChangeTutor[] {
    let next = 0
    return (rows ?? [])
        .map((row): UISlotChangeTutor | null => {
            const tutor = tutorOf(row)
            if (tutor == null) return null
            const tone = row?.coloured ? SLOT_CHANGE_TUTOR_TONES[next++ % SLOT_CHANGE_TUTOR_TONES.length]! : null
            return { ...tutor, tone, busyKeys: labels(row?.busy) }
        })
        .filter((tutor): tutor is UISlotChangeTutor => tutor != null)
}

/** Les horaires rangés par jour (lundi d'abord), chaque jour dans l'ordre de la journée. */
export function toUISlotChangeGrid(grid: ApiSlotChangeGrid | null | undefined): UISlotChangeGrid {
    const tutors = toUIGridTutors(grid?.tutors)
    const coloured = new Set(tutors.filter((tutor) => tutor.tone != null).map((tutor) => String(tutor.id)))
    const byDay = new Map<number, { slot: UISlotChangeGridSlot; minute: number }[]>()
    for (const row of grid?.slots ?? []) {
        if (!row?.key) continue
        const parts = weeklySlotParts(row.label, row.key)
        if (parts == null) continue
        const slot: UISlotChangeGridSlot = {
            key: row.key,
            label: row.label || row.key,
            course: row.course === true,
            // Seuls les tuteurs que la grille colorie portent une couleur sur la case.
            tutorIds: row.course ? [] : (row.tutor_ids ?? []).filter((id) => coloured.has(String(id))),
            courseLiveSessionIds: ids(row.course_live_session_ids),
        }
        const day = byDay.get(parts.weekday) ?? []
        day.push({ slot, minute: parts.hours * 60 + parts.minutes })
        byDay.set(parts.weekday, day)
    }
    const days: UISlotChangeGridDay[] = [...byDay.entries()]
        .sort(([a], [b]) => a - b)
        .map(([weekday, slots]) => ({
            id: weekdayKey(weekday),
            label: formatShortWeekday(weekday),
            slots: slots.sort((a, b) => a.minute - b.minute).map(({ slot }) => slot),
        }))
    return { days, tutors }
}

function toUIFallbacks(rows: ApiSlotChangeFallback[] | null | undefined): UISlotChangeFallback[] {
    return (rows ?? [])
        .map((row): UISlotChangeFallback | null => {
            const tutor = tutorOf(row?.tutor)
            if (!row?.key || tutor == null) return null
            return {
                key: row.key,
                tutor,
                subjects: subjectsOf(row.subjects),
                keptLiveSessionIds: ids(row.kept_live_session_ids),
                keptLabel: row.kept_label ?? "",
                keptStops: (row.kept_stops ?? [])
                    .filter((stop) => stop?.label && stop?.stops_on_label)
                    .map((stop) => ({ label: stop.label!, stopsOnLabel: stop.stops_on_label! })),
            }
        })
        .filter((fallback): fallback is UISlotChangeFallback => fallback != null)
}

/** La réponse pour les cours choisis : places, grille, replis, et combien d'horaires cocher. */
export function toUISlotChangeOptions(payload: ApiSlotChangePayload | null | undefined): UISlotChangeOptions {
    const minSlots = payload?.min_slots
    return {
        seats: toUISeats(payload?.seats),
        grid: toUISlotChangeGrid(payload?.grid),
        fallbacks: toUIFallbacks(payload?.fallbacks),
        minSlots: typeof minSlots === "number" && minSlots >= 1 ? Math.floor(minSlots) : 1,
    }
}

/** Ce que le serveur a fait ; `null` quand la réponse ne dit ni place ni proposition. */
export function toUISlotChangeOutcome(payload: ApiSlotChangeOutcome | null | undefined): UISlotChangeOutcome | null {
    if (payload?.outcome === "seat") {
        return {
            kind: "seat",
            changes: (payload.changes ?? [])
                .filter((change) => change?.from && change.to)
                .map((change) => {
                    const tutorGender = genderOf(change.tutor_gender)
                    return {
                        from: change.from ?? "",
                        to: change.to ?? "",
                        tutor: change.tutor ?? "",
                        ...(tutorGender != null ? { tutorGender } : {}),
                        effective: change.effective ?? "",
                        effectiveOn: change.effective_on ?? null,
                    }
                }),
            credits: typeof payload.credits === "number" && payload.credits > 0 ? payload.credits : 0,
            creditedWeeks: labels(payload.credited_weeks),
            // Un serveur d'avant ne le disait pas : il prévenait toujours le tuteur depuis l'espace parent.
            tutorsNotified: payload.tutors_notified !== false,
            mailSent: payload.mail_sent === true,
        }
    }
    if (payload?.outcome === "offer") {
        return {
            kind: "offer",
            offers: (payload.offers ?? [])
                .map((offer) => {
                    const tutor = tutorOf(offer?.tutor)
                    const slotLabels = (offer?.slots ?? [])
                        .map((slot) => slot?.label || slot?.key || "")
                        .filter((label) => label !== "")
                    return tutor == null || slotLabels.length === 0
                        ? null
                        : { id: offer.id ?? String(tutor.id), tutor, slotLabels }
                })
                .filter((offer): offer is NonNullable<typeof offer> => offer != null),
            held: (payload.held ?? []).map(heldOf).filter((held): held is NonNullable<typeof held> => held != null),
            mailSent: payload.mail_sent === true,
        }
    }
    return null
}

/** La demande en cours, ou `null` : sans phrase d'état, il n'y a rien à suivre. */
export function toUISlotChangePending(row: ApiSlotChangePending | null | undefined): UISlotChangePending | null {
    if (!row?.status_line) return null
    return {
        status: row.status === "cohort" ? "cohort" : "tutor",
        statusLine: row.status_line,
        courses: (row.courses ?? [])
            .filter((course) => !!course?.label)
            .map((course) => ({ label: course.label!, stopsOnLabel: course.stops_on_label ?? null })),
        proposed: (row.proposed ?? []).filter((slot): slot is string => !!slot),
    }
}
